import { CommonModule } from '@angular/common';
import type { SimpleChanges, TemplateRef } from '@angular/core';
import { ChangeDetectorRef, Component, Input, ViewChild, output } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';

import type { Subject } from 'rxjs';
import { merge as observableMerge } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { NgTemplateAssertType, noWhitespaceValidator } from '@core/common-legacy';
import type { Attribute } from '@core/http-types';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import type {
  DataTableCellContext,
  DataTableColumn,
  DataTableRow,
} from '@core/user-interface-legacy';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  CircularProgressIndicatorComponent,
  DataTableComponent,
  GroupComponent,
  IconButtonComponent,
  InputComponent,
  PlaceloaderComponent,
  RtTooltip,
} from '@core/user-interface-legacy';
import { LifeCycle } from '@core/utils-legacy';

import type { DepartmentsTableColumn } from '../../types/departments.types';
import { DepartmentsColumnId } from '../../types/departments.types';

@Component({
  selector: 'rt-departments-table',
  styleUrls: ['./departments-table.component.less'],
  templateUrl: './departments-table.component.html',
  imports: [
    ButtonComponent,
    ButtonLoadingDirective,
    CircularProgressIndicatorComponent,
    CommonModule,
    GroupComponent,
    IconButtonComponent,
    InputComponent,
    PlaceloaderComponent,
    ReactiveFormsModule,
    RtTooltip,
    DataTableComponent,
    NgTemplateAssertType,
    TranslatePipe,
  ],
})
export class DepartmentsTableComponent {
  public readonly CellContext: DataTableCellContext<Attribute & { id: number }>;
  public readonly DepartmentsColumnId: typeof DepartmentsColumnId = DepartmentsColumnId;

  public placeloaderRows: DataTableRow[];
  public templates: {
    [key: string]: TemplateRef<DataTableCellContext<Attribute & { id: number }>>;
  };
  public valueControl: UntypedFormControl = new UntypedFormControl(null, [
    Validators.required,
    noWhitespaceValidator,
  ]);
  public selectedIdsDict: Record<number, boolean> = {};

  @ViewChild('valueTemplate', { static: true }) public valueTemplate: TemplateRef<
    DataTableCellContext<Attribute & { id: number }>
  >;
  @ViewChild('countTemplate', { static: true }) public countTemplate: TemplateRef<
    DataTableCellContext<Attribute & { id: number }>
  >;
  @ViewChild('jobsCountTemplate', { static: true }) public jobsCountTemplate: TemplateRef<
    DataTableCellContext<Attribute & { id: number }>
  >;
  @ViewChild('talentPoolsCountTemplate', { static: true })
  public talentPoolsCountTemplate: TemplateRef<DataTableCellContext<Attribute & { id: number }>>;

  @Input() public columns: DepartmentsTableColumn[] = [];
  @Input() public data: Array<Attribute & { id: number }> = [];
  @Input() public ongoingEdit: Attribute;
  @Input() public ongoingSave: Array<Attribute['id']> = [];
  @Input() public translationsProgressMap: Record<
    string | number,
    {
      total: number;
      translated: number;
      percent: number;
    }
  >;

  @Input() public selected: { [key: string]: boolean | Attribute };

  @Input() public editable: boolean = false;
  @Input() public loading: boolean = false;
  @Input() public placeloaderRowsCount: number = 5;

  public rowSelect = output<number>();
  public edit = output<Attribute>();
  public save = output<Attribute>();
  public sortChange = output<DataTableColumn<DepartmentsColumnId>>();

  @LifeCycle('onDestroy')
  private ngOnDestroy$: Subject<void>;

  constructor(
    private cdRef: ChangeDetectorRef,
    private i18n: I18nService,
  ) {}

  public ngOnInit(): void {
    observableMerge(this.valueControl.valueChanges, this.valueControl.statusChanges)
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(() => this.cdRef.detectChanges());

    this.templates = {
      [DepartmentsColumnId.Value]: this.valueTemplate,
      [DepartmentsColumnId.Count]: this.countTemplate,
      [DepartmentsColumnId.JobsCount]: this.jobsCountTemplate,
      [DepartmentsColumnId.TalentPoolsCount]: this.talentPoolsCountTemplate,
    };
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('ongoingEdit' in changes) {
      const formVal = changes.ongoingEdit.currentValue
        ? changes.ongoingEdit.currentValue.value
        : null;
      this.valueControl.reset(formVal);
    }

    if (changes.selected) {
      this.selectedIdsDict = Object.entries(this.selected).reduce(
        (acc, [key, value]) => ({ ...acc, [key]: Boolean(value) }),
        {},
      );
    }

    if (changes.placeloaderRowsCount) {
      this.placeloaderRows = [...Array(this.placeloaderRowsCount)].map((x, i) => ({ id: i }));
    }
  }

  public isEdited(id: number | null): boolean {
    return this.ongoingEdit && this.ongoingEdit.id === id;
  }

  public isSaved(id: number): boolean {
    return this.ongoingSave.includes(id);
  }

  public getRandomWidth(min: number = 100, max: number = 200): string {
    const width = Math.floor(Math.random() * (max - min + 1)) + min;

    return `${width}px`;
  }

  public getLangTooltip(id: number): string {
    return `${this.translationsProgressMap[id].translated} / ${
      this.translationsProgressMap[id].total
    } ${this.i18n.translate('app.languages.fields_translated')}`;
  }
}
